import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-final-form';
import classNames from 'classnames';
import colors from 'constants/colors';
import { Image } from 'components/Image';
import Icon, { IconNames } from 'components/Icon';
import { formatNumberAsCurrency } from 'helpers/utils';
import { LeftContainer } from './LeftContainer';
import { ListRecipients } from './ListRecipients';
import { CompactRecipients } from './CompactRecipients';
import { useAppSelector } from 'helpers/hooks';
import { getRecipientsData } from 'selectors/recipients';
import { IRecipients } from 'store/recipients/types';
import { ListRecipientsDetailed } from './ListRecipientsDetailed';
import { useFieldValue } from 'hooks/useFormHelpers';

interface Props {
  isPreviewVisible: boolean;
  pageType: string;
}

export const ContentPreview: FC<Props> = ({ isPreviewVisible, pageType }) => {
  const form = useForm();
  const [candidates, setCandidates] = useState<IRecipients[]>([]);
  const recipients = useAppSelector(getRecipientsData);

  const recipientsSelected = useFieldValue('recipients_selected');

  useEffect(() => {
    if (recipientsSelected?.length > 0 && recipients.length > 0) {
      const orderedCandidates = recipientsSelected
        .map(recSel => recipients.find(rec => rec.recipient_id === recSel.value))
        .filter(Boolean);

      setCandidates(orderedCandidates);
    }
  }, [recipients, recipientsSelected]);

  return (
    <div
      className={classNames('content-preview', {
        'show-preview': isPreviewVisible,
        'display-none': !isPreviewVisible,
      })}
    >
      <div className="site-view">
        <img src={require('assets/img/Header.png')} alt="site-preview" className="header-preview" />
        <div className="row">
          <LeftContainer pageType={pageType} />
          <div
            className="right-container"
            style={{
              background:
                (form.getState().values?.page_type === 'LIST' && pageType !== 'event') ||
                pageType === 'recommendation'
                  ? colors.softGray
                  : colors.white,
            }}
          >
            {pageType === 'event' && (
              <>
                <div className="oath-utility inkBlue">Select your Ticket Option</div>
                <div className="oath-preview-text gray">
                  Once you select your ticket options, we will direct you to the donation checkout.
                </div>
                <div className="ticket-options">
                  {form
                    .getState()
                    .values?.rows?.filter(row => row.status === 'ACTIVE')
                    ?.map((row, index) => (
                      <div key={`ticket-option-${index}`} className="ticket-option">
                        <div className="row space-between">
                          <div className="oath-preview-text inkBlue">{row.name}</div>
                          <Image src={require('assets/img/Increment.png')} alt="increment" />
                        </div>
                        <div className="gray">{row.description}</div>
                        <div className="oath-preview-text inkBlue">
                          {formatNumberAsCurrency(row.price)}
                        </div>
                      </div>
                    ))}
                  <div className="ticket-option">
                    <div className="oath-preview-text inkBlue">Donate</div>
                    <div className="gray">
                      Make an additional donation or donate without attending event.
                    </div>
                    <div className="oath-preview-text inkBlue">Enter your amount</div>
                  </div>
                </div>
                <div className="donate-button">Proceed to Checkout</div>
                <br />
              </>
            )}
            {pageType === 'recommendation' && form.getState().values?.recipient_details ? (
              <>
                <div className="row space-between">
                  <div className="oath-preview-text">
                    {Object.keys(form.getState().values?.recipient_details).length} Recipients
                  </div>
                  <div className="row gap4 align-center">
                    <div className="oath-preview-text">Donation Amounts</div>
                  </div>
                </div>
                <ListRecipientsDetailed
                  candidates={candidates}
                  recipientDetails={form.getState().values?.recipient_details}
                />
              </>
            ) : (
              <>
                {form.getState().values?.page_type === 'LIST' ? (
                  <>
                    {pageType !== 'event' && (
                      <>
                        <div className="oath-utility gray">{form.getState().values?.page_cta}</div>
                        <div className="oath-utility gray">
                          Oath analyzes thousands of races to help you maximize your impact...
                        </div>
                      </>
                    )}
                    <div className="row space-between">
                      <div className="oath-preview-text">Recipient</div>
                      <div className="row gap4 align-center">
                        <div className="oath-preview-text">Impact Score</div>
                        <Icon name={IconNames.HelpCircle} size={12} />
                      </div>
                    </div>
                    <ListRecipients
                      candidates={candidates}
                      impactScoreVisibility={form.getState().values?.impact_score_visibility}
                    />
                    {pageType !== 'event' && <div className="donate-button">Donate</div>}
                  </>
                ) : (
                  <>
                    <div className="oath-preview-text">
                      Your contribution will be split between{' '}
                      <span className="title">{candidates.length} recipients.</span>
                    </div>
                    <div className="oath-preview-text">
                      The split will be even unless customized below.
                    </div>
                    <div className="compact-recipients">
                      <div className="oath-preview-text">
                        {candidates.length} out of {candidates.length} recipients selected:
                      </div>
                      <CompactRecipients candidates={candidates} />
                    </div>
                    {pageType !== 'event' && (
                      <div className="compact-amounts">
                        <div className="oath-preview-text">Select total contribution amount:</div>
                        <div className="amount-grid">
                          {form.getState().values?.donation_amounts?.map((amount, index) => (
                            <div className="amount-container" key={`amount-container-${index}`}>
                              <div className="oath-preview-text">
                                {formatNumberAsCurrency(amount)}
                              </div>
                            </div>
                          ))}
                          <div className="amount-container">
                            <div className="oath-preview-text" style={{ letterSpacing: -2 }}>
                              $_____
                            </div>
                          </div>
                        </div>
                        <div className="donate-button">Donate</div>
                      </div>
                    )}
                  </>
                )}
                <div className="rules">
                  <span>...</span>
                  <span>
                    By proceeding with this transaction, you agree to Oath&apos;s terms &
                    conditions.
                  </span>
                </div>
              </>
            )}
          </div>
          <img
            src={require('assets/img/Footer.png')}
            alt="site-preview"
            className="header-preview"
          />
        </div>
      </div>
    </div>
  );
};
